// ProgressStatus.jsx
import { useState, useEffect, useRef } from "react";

const FAKE_MESSAGES = [
  // Early phase (warm-up / parsing)
  "Hang tight, AI is thinking...",
  "Your document is in good hands…",
  "Give us a moment while we work our magic…",
  "Analyzing document structure...",
  "Scanning sections and headings...",
  "Reading through your resume...",
  "This resume has character.",
  "Interesting resume… analysing further 👀",
  "Identifying key segments...",
  "Scanning for brilliance…",
  "Someone’s been busy 👏",
  "Reading between the bullet points…",

  // Mid phase (core processing)
  "Extracting important details...",
  "Understanding your work experience...",
  "Your experience section came prepared.",
  "Analysing achievements and humble brags…",
  "Mapping roles and responsibilities...",
  "Cross-referencing your skills & roles...",
  "Detecting relevant technologies...",
  "Interpreting project information...",
  "Matching experience to known patterns...",
  "Organizing extracted data...",
  "Structuring professional timeline...",
  "Analyzing your professional plot twists…",
  "Converting document into career intelligence…",
  "Your resume and our AI are having a meeting.",
  "Analyzing skills, experience, and suspiciously high productivity…",
  "Your bullet points are under investigation.",
  "Searching for the secret ingredient: impact.",
  "Making sense of corporate acronyms…",

  // Deep processing (LLM-heavy feel)
  "Refining extracted insights...",
  "Validating context and accuracy...",
  "Resolving ambiguous entries...",
  "Aligning data with standard formats...",
  "Processing ambition, confidence, and caffeine…",
  "Detecting traces of leadership…",
  "Cross-checking buzzwords with reality…",
  "Evaluating your main-character energy…",
  "This resume might actually impress HR.",
  "The AI has opinions already.",
  "Looking for ‘team player’… found it.",
  "Optimizing structured output...",
  "This could’ve been an email. Good thing it’s a resume.",

  // Late phase (closing)
  "Almost there, finalizing extraction...",
  "Cleaning up the results...",
  "Preparing final structured data...",
  "Running final validation checks...",
  "Wrapping up, one moment...",
];
const CIRCUMFERENCE = 2 * Math.PI * 36;

const ProgressStatus = ({ message = "Processing...", percentage = 0, className = "", totalSteps = 5 }) => {
  const [displayPct, setDisplayPct] = useState(0);
  const [displayMsg, setDisplayMsg] = useState(message);

  const pctRef = useRef(0);
  const fakeTimer = useRef(null);
  const msgTimer = useRef(null);
  const msgIdx = useRef(0);
  const prevPct = useRef(0);

  function stopFake() {
    clearInterval(fakeTimer.current);
    clearInterval(msgTimer.current);
  }

  function applyPct(pct, msg) {
    const v = Math.min(100, Math.max(0, pct));
    pctRef.current = v;
    setDisplayPct(v);
    if (msg !== undefined) setDisplayMsg(msg);
  }

  function startFake(fromPct, ceiling) {
    stopFake();
    const cap = ceiling - 2;

    msgTimer.current = setInterval(() => {
      msgIdx.current = (msgIdx.current + 1) % FAKE_MESSAGES.length;
      setDisplayMsg(FAKE_MESSAGES[msgIdx.current]);
    }, 4000);

    fakeTimer.current = setInterval(() => {
      if (pctRef.current >= cap) {
        stopFake();
        return;
      }
      const remaining = cap - pctRef.current;
      const increment = Math.max(0.05, remaining * 0.012);
      applyPct(pctRef.current + increment);
    }, 120);
  }

  useEffect(() => {
    const prev = prevPct.current;
    prevPct.current = percentage;

    stopFake();
    applyPct(percentage, message);

    if (percentage < 100) {
      const stepSize = percentage - prev || 20;
      const nextReal = Math.min(percentage + stepSize, 100);
      startFake(percentage, nextReal);
    }

    return () => stopFake();
  }, [percentage, message]);

  const activeStep = Math.round(displayPct / (100 / totalSteps));

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      {/* message — fixed height so layout doesn't jump */}
      <p className="text-text-primary text-sm font-medium text-center max-w-[300px] min-h-[36px] flex items-center justify-center">{displayMsg}</p>

      {/* dots + percentage on one line, properly separated */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 w-40">
          {Array.from({ length: totalSteps }).map((_, i) => {
            const isActive = i < activeStep;
            const isCurrent = i === activeStep - 1;

            return (
              <div key={i} className={`flex-1 h-1 rounded-full transition-all duration-300 relative overflow-hidden ${isActive ? "" : "bg-gray-200"}`}>
                {isActive && (
                  <div
                    className={`absolute inset-0 ${isCurrent ? "animate-pulse" : ""}`}
                    style={{
                      background: "linear-gradient(90deg, #456A84 0%, #87AA92 100%)",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        {/* <span className="text-text-secondary text-xs tabular-nums">{Math.floor(displayPct)}%</span> */}
      </div>
    </div>
  );
};

export default ProgressStatus;
